// EmployeeList.tsx - Now just handles fetching & page layout
import React, { useContext, useEffect, useState, useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import NoEmployeeListFound from "./NoEmployeeListFound";
import AddEmployeeModal from "./AddEmployeeModal";
import AppButton from "../common/AppButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import MetricLedger from "../common/MetricLedger/MetricLedger";
import { getMetricPeriodLabel } from "../Dashboard/utils/metricsUtils";
import { useDashboardData } from "../../hooks/api/useDashboardData";
import DashboardHeader from "../Dashboard/DashboardHeader";
import { setMetricTimeRange } from "../../store/slices/dashboardSlice";
import { useGetCompanyEmployeeList } from "../../hooks/api/useEmployee";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import EmployeeTable from "./EmployeeListTable";
import ExportData from "../common/ExportData/ExportData";
import { exportEmployeeColumns } from "../common/constants/exportColumns";
import {
  KivuI18nContext,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useOrganizationLabels } from "../../hooks/useOrganizationLabels";

const EmployeeList: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useKivunovaTranslation();
  const org = useOrganizationLabels();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const employeeList = useSelector(
    (state: RootState) => state.employees.employeeList
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const [openAddModal, setOpenAddModal] = useState(false);
  const useGetCompanyEmployeeListMutation = useGetCompanyEmployeeList();
  const theme = useTheme();
  const { employeeKpis } = useDashboardData();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const metricTimeRangeFromState = useSelector(
    (state: RootState) => state.dashboard
  ).metricTimeRange;

  // Convert ISO strings back to Date objects for component usage
  const metricTimeRange = useMemo(
    () => ({
      startDate: new Date(metricTimeRangeFromState.startDate),
      endDate: new Date(metricTimeRangeFromState.endDate)
    }),
    [metricTimeRangeFromState.startDate, metricTimeRangeFromState.endDate]
  );

  useEffect(() => {
    (async () => {
      try {
        if (userDetails?.companyId) {
          await useGetCompanyEmployeeListMutation.mutateAsync({
            companyId: userDetails.companyId
          });
        }
      } catch (e) {
        console.error("Failed to fetch employee list", e);
      }
    })();
  }, [userDetails?.companyId, openAddModal]);

  if (useGetCompanyEmployeeListMutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "fetching-employee-list",
          "Fetching employee list..."
        )}
      />
    );
  }

  if (!employeeList.length) {
    return <NoEmployeeListFound />;
  }

  return (
    <Box
      data-testid="company-employee-list"
      sx={{ minHeight: "calc(100vh - 110px)", width: "100%", mt: 1 }}
    >
      <Box gap={4} flexDirection="column" display="flex">
        {/* Top Controls */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexDirection={matchMD ? "column" : "row"}
        >
          <Box display="flex" gap={2}>
            <AppButton variant="primary" onClick={() => setOpenAddModal(true)}>
              {t("add-new-employee", "Add New Employee")}
            </AppButton>
            <ExportData
              data={employeeList}
              columns={exportEmployeeColumns}
              filename={`employees_${new Date().toISOString().split("T")[0]}`}
              title={t("export-employee-list", "Export Employee List")}
              buttonVariant="outlined"
              buttonText={t("export", "Export")}
              formats={["csv", "xlsx", "pdf", "json"]}
              onExportComplete={(format) => {
                console.log(
                  `Exported ${employeeList.length} employees as ${format}`
                );
              }}
              onExportError={(error) => {
                console.error("Export failed:", error);
              }}
            />
          </Box>
        </Box>

        {/* Metrics */}
        {!matchMD && (
          <Box>
            <DashboardHeader
              onRangeChange={(r) => dispatch(setMetricTimeRange(r))}
            />
            <MetricLedger
              kpis={employeeKpis}
              period={getMetricPeriodLabel(
                metricTimeRange.startDate,
                metricTimeRange.endDate,
                t
              )}
            />
          </Box>
        )}

        {/* THE REUSABLE TABLE */}
        <EmployeeTable
          employees={employeeList}
          title={org.employeesTitle}
          showPhoneColumn={true}
        />

        <AddEmployeeModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
        />
      </Box>
    </Box>
  );
};

export default EmployeeList;
